import { createReducer } from 'redux-starter-kit';
import { handleError } from '../../utils/handleError';
import i18next from 'i18next';
import Dict from '../../utils/Dict';
import { MonitoringApi } from '../../_helpers/service';

/**
 * Constants
 */
export const monitoringModule = 'monitoring';
const LOADING = `${monitoringModule}/LOADING`;
const LOADING_TABLE = `${monitoringModule}/LOADING_TABLE`;
const TABLE_GENDER_DATA = `${monitoringModule}/TABLE_GENDER_DATA`;
const TABLE_REGION_DATA = `${monitoringModule}/TABLE_REGION_DATA`;
const SET_DATA = `${monitoringModule}/SET_DATA`;
const CLEAR_STATE = `${monitoringModule}/CLEAR_STATE`;

/**
 * Reducer
 */
const initialState = {
  loading: true,
  loadingTable: false,
  data: [],
  tableGenderData: [],
  tableRegionData: {
    content: [],
    totalElements: 0
  }
};

export default createReducer(initialState, {
  [LOADING]: (state, action) => {
    state.loading = action.payload;
  },
  [SET_DATA]: (state, action) => {
    state.data = action.payload;
  },
  [LOADING_TABLE]: (state, action) => {
    state.loadingTable = action.payload;
  },
  [TABLE_GENDER_DATA]: (state, action) => {
    state.tableGenderData = action.payload;
  },
  [TABLE_REGION_DATA]: (state, action) => {
    const filter = action.filter || {};
    if (filter.pageNumber && filter.pageNumber !== 1) {
      state.tableRegionData.content = [
        ...state.tableRegionData.content,
        ...action.payload.content
      ];
    } else {
      state.tableRegionData = action.payload;
    }
  },
  [CLEAR_STATE]: () => initialState
});

/**
 * Actions
 */
export const clearState = () => ({ type: CLEAR_STATE });

export const loadData = filter => async dispatch => {
  try {
    const { fromDate, toDate } = filter;
    if (fromDate && toDate) {
      dispatch({ type: LOADING, payload: true });
      const dictServices = await Dict.itemsObject('gu_services');
      const dictKato = await Dict.itemsObject('60');
      const response = await MonitoringApi.loadData(fromDate, toDate);
      const data = response.data.content.map(item => {
        let result = {};
        for (const column of item.passportValues) {
          result[column.code] = column.value;
          if (column.code === 'service_type') {
            const value = column.value
              .replace('REQUEST_FORM_', '')
              .replace('request_form_', '');
            const serviceItem =
              dictServices[value.toUpperCase()] ||
              dictServices[value.toLowerCase()] ||
              {};
            result[column.code] = value;
            result.service = serviceItem;
            result.ru_serviceName = serviceItem.ru_name;
            result.kk_serviceName = serviceItem.kk_name;
          }
          if (column.code === 'area_code') {
            const locationItem = dictKato[column.value] || {};
            result.location = locationItem.code || 'none';
            result.ru_locationName = locationItem.ru_name;
            result.kk_locationName = locationItem.kk_name;
          }
        }
        return result;
      });
      dispatch({ type: SET_DATA, payload: data });
    }
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING, payload: false });
  }
};

export const loadTableGender = () => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });
    let response = await MonitoringApi.loadGenderTable();
    const dictKato = await Dict.itemsObject('60');
    const data = response.data.content.map(item => {
      let result = {};
      for (const column of item.passportValues) {
        result[column.code] = column.value;
        if (column.code === 'legal_locality') {
          const locationItem = dictKato[column.value] || {};
          result.location = locationItem.code || 'none';
          result.ru_locationName = locationItem.ru_name;
          result.kk_locationName = locationItem.kk_name;
        }
      }
      return result;
    });
    dispatch({ type: TABLE_GENDER_DATA, payload: data });
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const loadTableRegion = filter => async dispatch => {
  try {
    dispatch({ type: LOADING_TABLE, payload: true });
    let response = await MonitoringApi.loadRegionTable(JSON.stringify(filter));
    const dictKato = await Dict.itemsObject('60');
    const data = response.data.content.map(item => {
      let result = {};
      for (const column of item.passportValues) {
        if (column.code === 'legal_locality') {
          result.district = dictKato[result.legal_locality] || {};
          result.location =
            dictKato[result.legal_locality?.slice(0, 4).padEnd(9, '0')] || {};
        } else {
          result[column.code] = column.value;
        }
      }
      return result;
    });
    dispatch({
      type: TABLE_REGION_DATA,
      payload: { content: [...data], totalElements: response.data.totalElements },
      filter
    });
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorLoadTable'));
  } finally {
    dispatch({ type: LOADING_TABLE, payload: false });
  }
};

export const exportGenderTableExcel =  async () => {
  try {
    const response = await MonitoringApi.getGenderTableExportExcelFile();
    const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `queued_by_gender_${new Date().toISOString().split('T')[0]}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode && link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorExportTable'));
  }
};

export const exportRegionTableExcel = async ()  => {
  try {
    const response = await MonitoringApi.getRegionTableExportExcelFile();
    const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `queued_by_region_${new Date().toISOString().split('T')[0]}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode && link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    handleError(error, i18next.t('monitoringDucks_errorExportTable'));
  }
};
